import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import ChessBoardView from '@/components/chess/ChessBoardView';
import { ChevronLeft, ChevronRight, GraduationCap, Flag, BookOpen, Compass, RotateCcw, Target } from 'lucide-react';

const START = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
const FILES = 'abcdefgh';

// Helpers FEN puros (movimiento libre en EXPLORE, sin motor nuevo ni validación).
function fenToMap(fen) {
  try {
    const [p] = String(fen).split(' ');
    const rk = p.split('/');
    const m = {};
    for (let r = 0; r < 8; r++) { let f = 0; for (const ch of rk[r]) { if (/\d/.test(ch)) f += parseInt(ch, 10); else { m[FILES[f] + (8 - r)] = ch; f++; } } }
    return m;
  } catch { return {}; }
}
function mapToFen(m, turn = 'w') {
  const rk = [];
  for (let r = 8; r >= 1; r--) { let row = '', e = 0; for (let f = 0; f < 8; f++) { const p = m[FILES[f] + r]; if (p) { if (e > 0) { row += e; e = 0; } row += p; } else e++; } if (e > 0) row += e; rk.push(row); }
  return `${rk.join('/')} ${turn} - - 0 1`;
}
const fenTurn = (fen) => (String(fen).split(' ')[1] === 'b' ? 'b' : 'w');

// EXPLORE · Posiciones educativas.
// Permite trabajar con FENs simplificados (por ejemplo, una sola pieza y sin reyes)
// sin alterar la posición visual diseñada por el profesor.
function exploreFenWithTurn(fen, turn) {
  const parts = String(fen || START).trim().split(/\s+/);
  while (parts.length < 6) parts.push(parts.length === 1 ? 'w' : '-');
  parts[1] = turn === 'b' ? 'b' : 'w';
  return parts.join(' ');
}

function explorePieceColor(fen, square) {
  try {
    const placement = String(fen || START).split(/\s+/)[0];
    const ranks = placement.split('/');
    const sq = String(square || '').toLowerCase();
    if (!/^[a-h][1-8]$/.test(sq)) return null;
    const rankIndex = 8 - Number(sq[1]);
    const row = ranks[rankIndex];
    if (!row) return null;

    let file = 0;
    for (const ch of row) {
      if (/^[1-8]$/.test(ch)) {
        file += Number(ch);
      } else {
        if (file === FILES.indexOf(sq[0])) {
          if (/^[A-Z]$/.test(ch)) return 'w';
          if (/^[a-z]$/.test(ch)) return 'b';
          return null;
        }
        file++;
      }
    }
  } catch {
    return null;
  }
  return null;
}

function createExploreChess(fen, pieceSquare = '') {
  const color = explorePieceColor(fen, pieceSquare);
  const normalizedFen = color ? exploreFenWithTurn(fen, color) : fen;
  return new Chess(normalizedFen, { skipValidation: true });
}

// Normaliza el challenge de un step (square/move) para el Viewer.
function normChallenge(s) {
  const ch = s?.challenge;
  if (ch && ch.type === 'move' && ch.from && ch.to) return { type: 'move', from: String(ch.from).toLowerCase(), to: String(ch.to).toLowerCase() };
  if (ch && ch.target) return { type: 'square', target: String(ch.target).toLowerCase() };
  return null;
}

// Normaliza la misión pedagógica de EXPLORE.
// No valida la misión aquí; solo prepara los datos para el Viewer.
function normExplore(s) {
  if (s?.mode !== 'explore') return null;
  const ex = s?.explore || {};
  const type = ['mark', 'path', 'discover'].includes(ex.type) ? ex.type : 'mark';
  const targets = Array.isArray(ex.targets)
    ? [...new Set(ex.targets.map(v => String(v || '').toLowerCase()).filter(sq => /^[a-h][1-8]$/.test(sq)))]
    : [];
  const piece_square = /^[a-h][1-8]$/.test(String(ex.piece_square || '').toLowerCase())
    ? String(ex.piece_square).toLowerCase()
    : '';
  return { type, targets, piece_square };
}

// Explanation V2 · Etapa 1 — Viewer legacy-safe (SOLO OBSERVAR).
// Reutiliza EXCLUSIVAMENTE ChessBoardView. No hay interacción, scoring ni retos.
// - Explanation con `steps[]` → navega step a step.
// - Explanation legacy (title/fen/pgn/text sin steps) → un único "Step virtual".
// NO modifica datos legacy (solo lectura en memoria).

// Convierte una actividad explanation a una lista normalizada de steps.
function normalizeSteps(activity) {
  const steps = Array.isArray(activity?.steps) ? activity.steps : null;
  if (steps && steps.length > 0) {
    return steps.map((s, i) => ({
      id: s.id || `step-${i}`,
      title: s.title || `Paso ${i + 1}`,
      rich_text: s.rich_text || '',
      board: s.board && s.board.fen ? s.board : null,
      mode: s.mode || 'explain',
      challenge: normChallenge(s),
      explore: normExplore(s),
    }));
  }
  // Legacy → un único step virtual (NO persiste, solo en memoria).
  return [{
    id: 'legacy',
    title: activity?.title || 'Explicación',
    rich_text: activity?.text || '',
    board: activity?.fen ? { fen: activity.fen, orientation: 'white', arrows: [], circles: {}, square_styles: {}, show_coordinates: true } : null,
    mode: 'explain',
    legacyPgn: activity?.pgn || '',
  }];
}

// Board del step activo. Memoizado para no re-renderizar por cambios ajenos.
// En EXPLORE `fen` refleja el estado temporal del alumno; las anotaciones
// (arrows/circles/square_styles) SIEMPRE son las oficiales del profesor.
const StepBoard = React.memo(function StepBoard({
  board, fen, draggable, onPieceDrop, onSquareClick, boardWidth,
  highlightStyles, allowAnnotations = false, allowLeftClickAnnotations = false,
  conventionalLiveAnnotations = false,
  rightClickArrowOnly = false, onAnnotationsChange
}) {
  return (
    <ChessBoardView
      fen={fen || board.fen || START}
      orientation={board.orientation === 'black' ? 'black' : 'white'}
      arrows={Array.isArray(board.arrows) ? board.arrows : []}
      circles={board.circles && typeof board.circles === 'object' ? board.circles : {}}
      allowAnnotations={allowAnnotations}
      allowLeftClickAnnotations={allowLeftClickAnnotations}
      conventionalLiveAnnotations={conventionalLiveAnnotations}
      rightClickArrowOnly={rightClickArrowOnly}
      onAnnotationsChange={onAnnotationsChange}
      extraSquareStyles={{ ...(board.square_styles && typeof board.square_styles === 'object' ? board.square_styles : {}), ...(highlightStyles || {}) }}
      boardWidth={boardWidth}
      arePiecesDraggable={!!draggable}
      onPieceDrop={draggable ? onPieceDrop : undefined}
      onSquareClick={onSquareClick}
    />
  );
});

export default function ExplanationViewer({
  open,
  activity,
  onClose,
  onChallengeResult,
  onExploreComplete,
  onExploreProgress,
  showChrome = true,
  liveProgress = null,
  challengeIntroVisible = false,
  teacherPanel = null,
  teacherNavigation = null
}) {
  const isStudentLive = typeof onChallengeResult === 'function';
  const isTeacherLive = showChrome === false && !isStudentLive;

  const steps = useMemo(() => (activity ? normalizeSteps(activity) : []), [activity]);
  const [idx, setIdx] = useState(0);
  const annotationStep = steps[idx] || steps[0];
  const annotationKey = `${activity?.id || 'activity'}:${annotationStep?.id || idx}`;
  const [teacherAnnotations, setTeacherAnnotations] = useState(null);
  // Posición temporal del profesor en Explanation Live; nunca se guarda en el estudio.
  const [teacherLiveFen, setTeacherLiveFen] = useState(null);
  // EXPLORE: estado temporal del alumno. NO se persiste; se reinicia al
  // cambiar de step / actividad / al reabrir. Nunca toca board.fen oficial.
  const [exploreFen, setExploreFen] = useState(null);
  // EXPLORE: objetivos descubiertos por el alumno. Solo estado temporal.
  const [exploreFound, setExploreFound] = useState([]);
const [exploreSelectedSquare, setExploreSelectedSquare] = useState(null);
  // CHALLENGE (type 'square'): estado local idle|correct|incorrect. No persiste,
  // no hay scoring/intentos. Se reinicia al cambiar de escena.
  const [challengeStatus, setChallengeStatus] = useState('idle');
  // CHALLENGE (type 'square') · mejora visual: casilla que el alumno acaba de
  // seleccionar (minúsculas). Se resalta verde/rojo según challengeStatus.
  // Cada intento REEMPLAZA al anterior (no es historial). No afecta validación.
  const [challengePick, setChallengePick] = useState(null);
  // CHALLENGE (type 'move'): FEN temporal del alumno (no persiste, no toca el oficial).
  const [challengeMoveFen, setChallengeMoveFen] = useState(null);
  const total = steps.length;

  // Notifica una sola vez cuando una misión Explore queda completada.
  // Reset al abrir / cambiar de actividad.
  useEffect(() => { if (open) setIdx(0); }, [open, activity?.id]);
  // Reinicia el estado temporal de EXPLORE/CHALLENGE en cada cambio de escena.
  useEffect(() => { setExploreFen(null); setExploreFound([]);setExploreSelectedSquare(null); setChallengeStatus('idle'); setChallengeMoveFen(null); setChallengePick(null); setTeacherLiveFen(null); }, [idx, activity?.id, open]);
  // Las anotaciones del profesor son temporales. Cada apertura o cambio de
  // paso parte de las anotaciones originales del estudio, sin modificarlas.
  useEffect(() => {
    if (!open || !activity) {
      setTeacherAnnotations(null);
      return;
    }

    const sourceBoard = annotationStep?.board;
    setTeacherAnnotations({
      key: annotationKey,
      arrows: Array.isArray(sourceBoard?.arrows)
        ? sourceBoard.arrows.map(a => Array.isArray(a) ? [...a] : a)
        : [],
      circles: sourceBoard?.circles && typeof sourceBoard.circles === 'object'
        ? { ...sourceBoard.circles }
        : {}
    });
  }, [open, activity?.id, annotationKey]);

  const handleTeacherAnnotationsChange = (arrows, circles) => {
    setTeacherAnnotations({ key: annotationKey, arrows, circles });
  };

  // Tablero responsivo (mismo criterio que los runners): mide el contenedor.
  const boardWrapRef = useRef(null);
  const [boardPx, setBoardPx] = useState(440);
  useEffect(() => {
    if (!open) return;

    const isTeacherExplore = isTeacherLive && isExplore;
    const maxBoard = isTeacherExplore ? 480 : (isTeacherLive ? 560 : (isStudentLive ? 645 : (showChrome ? 520 : 600)));
    let ro;
    let rafId;

    const measure = () => {
      const el = boardWrapRef.current;
      if (!el) return;

      const w = el.clientWidth || 0;
      const mobile = typeof window !== 'undefined'
        && window.matchMedia('(max-width: 767px)').matches;

      if (w > 0) {
        const available = mobile
          ? Math.min(w, window.innerWidth - 32, 360)
          : Math.min(w, maxBoard, window.innerHeight - 300);

        setBoardPx(Math.max(240, Math.floor(available)));
      }
    };

    rafId = window.requestAnimationFrame(measure);

    const el = boardWrapRef.current;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }

    window.addEventListener('resize', measure);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [open, idx, showChrome, isTeacherLive]);

  if (!activity) return null;

  const step = steps[idx] || steps[0];
  const isFirst = idx === 0;
  const isLast = idx >= total - 1;
  const progressPct = total > 0 ? Math.round(((idx + 1) / total) * 100) : 0;
  const hasBoard = !!step?.board;
  const isExplore = step?.mode === 'explore';
  const isChallenge = step?.mode === 'challenge';
  const teacherAnnotationActive = isTeacherLive && !isExplore && !isChallenge;
  const annotationsMatchStep = teacherAnnotations?.key === annotationKey;
  const boardForRender = teacherAnnotationActive && annotationsMatchStep
    ? {
        ...step.board,
        arrows: teacherAnnotations.arrows,
        circles: teacherAnnotations.circles
      }
    : step.board;
  const challengeType = step?.challenge?.type === 'move' ? 'move' : 'square';
  const isChallengeMove = isChallenge && challengeType === 'move' && !!step?.challenge?.from && !!step?.challenge?.to;
  const isChallengeSquare = isChallenge && challengeType === 'square' && !!step?.challenge?.target;
  const exploreType = ['mark', 'path', 'discover'].includes(step?.explore?.type) ? step.explore.type : 'mark';
  const exploreTargets = Array.isArray(step?.explore?.targets) ? step.explore.targets : [];
  const explorePieceSquare = String(step?.explore?.piece_square || '').toLowerCase();
  const draggable = teacherAnnotationActive || (isExplore && exploreType === 'path') || isChallengeMove;
  // Cada Step utiliza su propio FEN; los movimientos del profesor son temporales.
  const baseFen = step?.board?.fen || START;
  const displayFen = teacherAnnotationActive && teacherLiveFen
    ? teacherLiveFen
    : (isExplore && exploreType === 'path' && exploreFen)
      ? exploreFen
      : (isChallengeMove && challengeMoveFen ? challengeMoveFen : baseFen);

  // Diagnóstico temporal del arrastre en Explanation Live.
  // Movimiento ilustrativo libre del profesor en Explanation Live.
  const handleTeacherLiveDrop = (from, to) => {
    if (!teacherAnnotationActive) return false;

    const f = String(from || '').toLowerCase();
    const t = String(to || '').toLowerCase();

    if (!/^[a-h][1-8]$/.test(f) ||
        !/^[a-h][1-8]$/.test(t) || f === t) {
      return false;
    }

    const currentFen = displayFen || START;
    const pieces = fenToMap(currentFen);
    const piece = pieces[f];

    if (!piece) return false;

    delete pieces[f];
    pieces[t] = piece;

    const nextFen = mapToFen(pieces, fenTurn(currentFen));
    setTeacherLiveFen(nextFen);

    return true;
  };

  const handleExploreDrop = (from, to) => {
    if (!isExplore || exploreType !== 'path') return false;
    const f = String(from).toLowerCase();
    const t = String(to).toLowerCase();
    try {
      const pieceSquare = explorePieceSquare || f;
      const c = createExploreChess(displayFen, pieceSquare);
      const mv = c.move({ from: f, to: t, promotion: 'q' });
      if (!mv) return false;

      // En Path no existe un rival que juegue entre movimientos.
      // Conservamos el turno de la pieza que el alumno está recorriendo.
      const pieceColor = explorePieceColor(displayFen, f);
      const nextFen = pieceColor
        ? exploreFenWithTurn(c.fen(), pieceColor)
        : c.fen();

      setExploreFen(nextFen);
      if (exploreTargets.includes(t) && !exploreFound.includes(t)) {
        const nextFoundCount = exploreFound.length + 1;
        setExploreFound(prev => [...prev, t]);
        onExploreProgress?.(nextFoundCount);

        if (nextFoundCount >= exploreTargets.length) {
          onExploreComplete?.();
        }
      }
      return true;
    } catch {
      return false;
    }
  };

  const handleExploreClick = (sq) => {
    if (!isExplore) return;
    const square = String(sq).toLowerCase();

    if (exploreType === 'mark') {
      if (exploreTargets.includes(square) && !exploreFound.includes(square)) {
        const nextFoundCount = exploreFound.length + 1;
        setExploreFound(prev => [...prev, square]);
        onExploreProgress?.(nextFoundCount);

        if (nextFoundCount >= exploreTargets.length) {
          onExploreComplete?.();
        }
      }
      return;
    }

  if (exploreType === 'path') {
    if (!exploreSelectedSquare) {
      const pieces = fenToMap(displayFen);
      if (pieces[square]) {
        setExploreSelectedSquare(square);
      }
      return;
    }

    if (square === exploreSelectedSquare) {
      setExploreSelectedSquare(null);
      return;
    }

    const moved = handleExploreDrop(exploreSelectedSquare, square);

    if (moved) {
      setExploreSelectedSquare(null);
    }

    return;
  }
if (exploreType === 'discover') {
      if (!explorePieceSquare) return;
      try {
        const c = createExploreChess(baseFen, explorePieceSquare);
        const legal = c.moves({ square: explorePieceSquare, verbose: true }).map(m => m.to.toLowerCase());
        if (legal.includes(square) && !exploreFound.includes(square)) {
          const nextFoundCount = exploreFound.length + 1;
          setExploreFound(prev => [...prev, square]);
          onExploreProgress?.(nextFoundCount);

          if (nextFoundCount >= legal.length) {
            onExploreComplete?.();
          }
        }
      } catch {
        // La misión inválida no bloquea el Viewer.
      }
    }
  };

  const discoverLegalTargets = (() => {
    if (!isExplore || exploreType !== 'discover' || !explorePieceSquare) return [];
    try {
      const c = createExploreChess(baseFen, explorePieceSquare);
      return c.moves({ square: explorePieceSquare, verbose: true }).map(m => m.to.toLowerCase());
    } catch {
      return [];
    }
  })();

  const exploreMissionTargets = exploreType === 'discover' ? discoverLegalTargets : exploreTargets;
  const exploreComplete = isExplore && exploreMissionTargets.length > 0 && exploreFound.length >= exploreMissionTargets.length;

  const handleChallengeMoveDrop = (from, to) => {
    const f = String(from).toLowerCase(), t = String(to).toLowerCase();
    const map = fenToMap(displayFen); const p = map[f]; if (!p) return false;
    delete map[f]; map[t] = p; setChallengeMoveFen(mapToFen(map, fenTurn(displayFen)));
    const status = f === step.challenge.from && t === step.challenge.to ? 'correct' : 'incorrect';
    setChallengeStatus(status);
    if (onChallengeResult) onChallengeResult(status);
    return true;
  };
  const challengeTarget = (step?.challenge?.target || '').toLowerCase();
  const handleChallengeClick = (sq) => {
    if (!isChallengeSquare) return;
    const status = String(sq).toLowerCase() === challengeTarget ? 'correct' : 'incorrect';
    setChallengeStatus(status);
    if (onChallengeResult) onChallengeResult(status);
  };
  // Wrapper visual: captura la casilla seleccionada (para el resaltado) y
  // delega la validación intacta a handleChallengeClick. NO cambia la lógica.
  const handleChallengeSquarePick = (sq) => {
    setChallengePick(String(sq).toLowerCase());
    handleChallengeClick(sq);
  };
  // Estilo temporal SOLO del intento actual: verde si correcto, rojo si
  // incorrecto. Cuando el alumno falla NO se revela la casilla correcta.
  const challengeHighlight = (isChallengeSquare && challengePick && challengeStatus !== 'idle')
    ? { [challengePick]: challengeStatus === 'correct'
        ? { backgroundColor: 'rgba(34,197,94,0.55)', boxShadow: 'inset 0 0 0 4px rgba(22,163,74,0.95)' }
        : { backgroundColor: 'rgba(239,68,68,0.50)', boxShadow: 'inset 0 0 0 4px rgba(220,38,38,0.95)' } }
    : null;
  const resetChallengeMove = () => { setChallengeMoveFen(null); setChallengeStatus('idle'); };

  const goPrev = () => setIdx((i) => Math.max(0, i - 1));
  const goNext = () => { if (isLast) { onClose?.(); } else { setIdx((i) => Math.min(total - 1, i + 1)); } };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent data-testid="explanation-viewer" className={`w-[calc(100vw-1rem)] ${isTeacherLive ? 'max-w-6xl' : 'max-w-5xl'} h-[90vh] max-h-[90vh] flex flex-col p-0 overflow-hidden gap-0 bg-white top-2 translate-y-0 ${isTeacherLive ? "sm:top-1/2" : "sm:top-1/2"} sm:translate-y-[-50%] ${showChrome ? '' : '[&>button]:hidden'}`}>
        <DialogTitle className="sr-only">{activity.title || 'Explicación'}</DialogTitle>
        <DialogDescription className="sr-only">Visor de explicación paso a paso.</DialogDescription>

        {/* Cabecera tipo "clase" */}
        <div className="shrink-0 bg-gradient-to-r from-indigo-600 to-violet-600 text-white px-4 py-3 sm:px-5 sm:py-3">
          <div className="relative flex items-center justify-center min-h-[28px] sm:min-h-0 sm:justify-between gap-3 text-indigo-100 text-[10px] sm:text-xs font-semibold uppercase tracking-widest">
            <div className="flex items-center gap-1.5 bg-white text-indigo-700 rounded-full px-2.5 py-0.5 shadow-sm sm:bg-transparent sm:text-indigo-100 sm:rounded-none sm:px-0 sm:py-0 sm:shadow-none sm:gap-2">
              <GraduationCap className="w-4 h-4 shrink-0" />
              <span className="whitespace-nowrap">Clase interactiva</span>
            </div>
            {liveProgress && (
              <div data-testid="student-progress-label" className="absolute right-0 top-1/2 -translate-y-1/2 shrink-0 text-[10px] sm:text-xs font-bold normal-case tracking-normal tabular-nums text-white whitespace-nowrap sm:static sm:translate-y-0">
                Paso {Math.max(1, Number(liveProgress.currentStep) + 1)} de {Math.max(1, Number(liveProgress.totalSteps) || 1)}
              </div>
            )}
          </div>
          <h2 data-testid="ev-activity-title" className="font-display font-black text-sm sm:text-xl leading-tight mt-2 sm:mt-1">{activity.title || 'Explicación'}</h2>
          <div className="mt-2 sm:mt-3 flex items-center gap-2 sm:gap-3">
            <div className="flex-1 h-1.5 sm:h-2 rounded-full bg-white/25 overflow-hidden">
              <div className="h-full bg-white transition-all duration-300" style={{ width: `${progressPct}%` }} />
            </div>
            <div data-testid="ev-progress" className="hidden sm:block text-sm font-bold whitespace-nowrap tabular-nums">Paso {idx + 1} de {total}</div>
          </div>
        </div>

        {/* Cuerpo: tablero protagonista + panel de explicación */}
        <div className={`flex-1 min-h-0 overflow-y-auto grid content-start gap-4 p-3 sm:p-5 ${hasBoard ? (isTeacherLive && teacherPanel ? 'md:grid-cols-[minmax(0,1fr)_minmax(8rem,10rem)_minmax(0,22rem)]' : 'md:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]') : 'grid-cols-1'}`}>
          {hasBoard && (
            <div ref={boardWrapRef} className="w-full min-w-0" data-testid="ev-board">
              <div className="grid place-items-center">
                <StepBoard
                  board={boardForRender}
                  fen={displayFen}
                  draggable={draggable}
                  onPieceDrop={teacherAnnotationActive ? handleTeacherLiveDrop : (isExplore ? handleExploreDrop : (isChallengeMove ? handleChallengeMoveDrop : undefined))}
                  onSquareClick={challengeIntroVisible ? undefined : (isChallengeSquare ? handleChallengeSquarePick : (isExplore ? handleExploreClick : undefined))}
                  highlightStyles={{
                    ...(challengeHighlight || {}),
                    ...(isExplore ? Object.fromEntries([
                      ...exploreFound.map(sq => [sq, {
                        backgroundColor: 'rgba(34,197,94,0.55)',
                        boxShadow: 'inset 0 0 0 4px rgba(22,163,74,0.95)'
                      }]),
                      ...(exploreType === 'discover' && explorePieceSquare ? [[explorePieceSquare, {
                        backgroundColor: 'rgba(245,158,11,0.22)',
                        boxShadow: 'inset 0 0 0 4px rgba(245,158,11,0.95)'
                      }]] : [])
                    ]) : {})
                  }}
                  boardWidth={boardPx}
                  allowAnnotations={teacherAnnotationActive}
                  allowLeftClickAnnotations={teacherAnnotationActive}
                  conventionalLiveAnnotations={teacherAnnotationActive}
                  rightClickArrowOnly={teacherAnnotationActive}
                  onAnnotationsChange={
                    teacherAnnotationActive ? handleTeacherAnnotationsChange : undefined
                  }
                />
                </div>
              {teacherAnnotationActive && (
                <div className="mt-3 flex justify-center">
                  <button
                    type="button"
                    data-testid="ev-teacher-reset-board"
                    onClick={() => setTeacherLiveFen(null)}
                    className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 transition-colors"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Reiniciar tablero
                  </button>
                </div>
              )}
              <>
                {isExplore && (
                    <div className="mt-2 mb-4 space-y-2 text-xs text-emerald-700">
                      <div className="flex items-center justify-center gap-2">
                        <span>
                          {exploreType === 'mark' && 'Marca las casillas objetivo.'}
                          {exploreType === 'path' && 'Lleva la pieza hasta las casillas objetivo con movimientos legales.'}
                          {exploreType === 'discover' && `Selecciona las casillas a las que creas que puede moverse la pieza de ${explorePieceSquare.toUpperCase()}.`}
                        </span>
                        <button
                          data-testid="ev-explore-reset"
                          onClick={() => { setExploreFen(null); setExploreFound([]); setExploreSelectedSquare(null); onExploreProgress?.(0); }}
                          className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-0.5 font-semibold hover:bg-emerald-100"
                        >
                          <RotateCcw className="w-3 h-3" /> Reiniciar
                        </button>
                      </div>
                      <div data-testid="ev-explore-progress" className="text-center font-semibold mt-2 pb-2">
                        Progreso: {Math.min(exploreFound.length, exploreMissionTargets.length)} / {exploreMissionTargets.length}
                      </div>
                      {exploreComplete && (
                        <div data-testid="ev-explore-complete" className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-center text-emerald-800 font-bold">
                          🎯 ¡Misión cumplida!
                          {exploreType === 'mark' && ' Has marcado todas las casillas.'}
                          {exploreType === 'path' && ' Has recorrido todas las casillas objetivo.'}
                          {exploreType === 'discover' && ' Has descubierto todos los movimientos legales.'}
                        </div>
                      )}
                    </div>
                  )}
                  {isChallengeMove && (
                    <div className="mt-2 flex items-center justify-center gap-2 text-xs text-amber-700">
                      <span>Arrastra la pieza para realizar el movimiento.</span>
                      <button data-testid="ev-challenge-move-reset" onClick={resetChallengeMove} className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 font-semibold hover:bg-amber-100"><RotateCcw className="w-3 h-3" /> Reiniciar</button>
                    </div>
                )}
              </>
            </div>
          )}

          {isTeacherLive && teacherPanel && (
            <div data-testid="teacher-responses-column" className="min-w-0">
              {teacherPanel}
            </div>
          )}
          <div className={`min-w-0 flex flex-col ${!hasBoard ? 'max-w-2xl mx-auto text-center' : ''}`} data-testid="ev-step-panel">
            {isChallenge ? (
              <div data-testid="ev-mode-badge" className="flex items-center gap-2 text-amber-600">
                <Target className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-widest">Reto</span>
              </div>
            ) : isExplore ? (
              <div data-testid="ev-mode-badge" className="flex items-center gap-2 text-emerald-600">
                <Compass className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-widest">Exploración</span>
              </div>
            ) : (
              <div data-testid="ev-mode-badge" className="flex items-center gap-2 text-indigo-600">
                <BookOpen className="w-4 h-4" />
                <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest">Explicación</span>
              </div>
            )}
            <h3 data-testid="ev-step-title" className="font-display font-extrabold text-base sm:text-lg leading-tight text-slate-900 mt-1">{step?.title}</h3>
            {step?.rich_text
              ? <div className={isStudentLive ? 'student-live-explanation-scroll' : ''}>
                  <div data-testid="ev-step-text" className={`prose prose-sm max-w-none text-slate-700 mt-1 sm:mt-2 leading-relaxed [&_h1]:text-base [&_h2]:text-base [&_h3]:text-base [&_h1]:leading-tight [&_h2]:leading-tight [&_h3]:leading-tight [&_p]:text-sm [&_p]:leading-relaxed [&_p]:my-2 md:[&_h1]:text-[12px] md:[&_h2]:text-[12px] md:[&_h3]:text-[12px] md:[&_p]:text-[12px] md:[&_li]:text-[12px] md:[&_h1]:leading-[1.4] md:[&_h2]:leading-[1.4] md:[&_h3]:leading-[1.4] md:[&_p]:leading-[1.6] md:[&_li]:leading-[1.6] md:[&_p]:my-1.5 [&_b]:font-bold [&_i]:italic` } dangerouslySetInnerHTML={{ __html: step.rich_text }} />
                </div>
              : (!isChallenge && <div data-testid="ev-step-text" className="text-slate-400 italic mt-3 text-sm">Sin texto para este paso.</div>)}
            {isChallenge && (
              <div className="mt-4" data-testid="ev-challenge">
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                  <div className="text-amber-800 font-semibold text-sm">{challengeType === 'move' ? '🎯 Realiza el movimiento indicado.' : '🎯 Haz clic en la casilla correcta.'}</div>
                </div>
                {challengeStatus === 'correct' && (
                  <div data-testid="ev-challenge-correct" className="mt-3 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-emerald-800 font-bold">{challengeType === 'move' ? '✅ ¡Correcto!' : `✅ ¡Correcto! La casilla es ${challengeTarget.toUpperCase()}.`}</div>
                )}
                {challengeStatus === 'incorrect' && (
                  <div data-testid="ev-challenge-incorrect" className="mt-3 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-rose-700 font-bold">❌ Inténtalo nuevamente.</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Navegación — chrome propio del Viewer. Se oculta con showChrome=false
            (p. ej. profesor en Classroom, que usa su barra fija oficial). */}
        {(showChrome || (isTeacherLive && teacherNavigation)) && (
          <div data-testid={isTeacherLive ? "teacher-explanation-controls" : undefined} className="shrink-0 flex items-center justify-between gap-3 border-t border-slate-200 px-4 py-2 sm:px-6 bg-slate-50">
            {isTeacherLive && teacherNavigation ? (
              <>
                <Button data-testid="tex-prev" size="sm" variant="outline"
                  onClick={teacherNavigation.onPrevious}
                  disabled={teacherNavigation.currentStep <= 0}
                  className="rounded-xl disabled:opacity-40">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                </Button>
                <span data-testid="tex-progress" className="text-sm font-bold tabular-nums px-2 whitespace-nowrap">
                  Paso {teacherNavigation.currentStep + 1} de {teacherNavigation.totalSteps}
                </span>
                <Button data-testid="tex-next" size="sm"
                  onClick={teacherNavigation.onNext}
                  disabled={teacherNavigation.currentStep >= teacherNavigation.totalSteps - 1}
                  className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white">
                  Siguiente <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
                <Button data-testid="tex-finish" size="sm" variant="destructive"
                  onClick={teacherNavigation.onFinish} className="rounded-xl">
                  <Flag className="w-4 h-4 mr-1" /> Finalizar
                </Button>
              </>
            ) : (
              <>
                <Button
                  data-testid="ev-prev"
                  variant="outline"
                  onClick={goPrev}
                  disabled={isFirst}
                  className="rounded-xl disabled:opacity-40"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                </Button>
                <Button
                  data-testid="ev-next"
                  onClick={goNext}
                  className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  {isLast ? <><Flag className="w-4 h-4 mr-1" /> Finalizar</> : <>Siguiente <ChevronRight className="w-4 h-4 ml-1" /></>}
                </Button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
